import { convertCategoryParametersToFlat, findArrValueIndex, searchParamsToObject } from './utils';
import type { CommonParams } from './convertParamsToPath';
import { CategoryIdDict, CategoryNameDict, DictCategory, Dicts, GeoDict, GeoItem, GeoType } from './types';
import { runtimeConfig } from '@/config/runtime-config';
import type { TelegramProductParameterValueResponseDto } from '@/generated';

const WL_COUNTRY = runtimeConfig.defaultCountry.normalizedName;

const LEGIT_SLUG_SEGMENT = /^[a-z0-9-]+$/;
const MAX_SLUG_DEPTH = 12;

export type CategoryItem = {
  id: string;
  name: string;
};

export type ParamItem = {
  id: string;
  value: string | string[] | { from?: number; to?: number };
};

const commonParamsKeys = ['skip', 'searchOnlyInTitle', 'minPrice', 'maxPrice', 'sortBy', 'search'];

type Breadcrumb = {
  url: string;
  name: string;
};

type Return = {
  geo: GeoItem;
  categories: CategoryItem[];
  params: ParamItem[];
  commonParams: CommonParams;
  breadcrumbs: Breadcrumb[];
  hasAnyParamInPath: boolean;
  pathParams: { id: string; name: string; searchKey: string }[];
};

export function convertPathToParams(
  slug: string[],
  searchParams: URLSearchParams,
  dicts: Dicts,
  locale: string,
  wlMode: boolean = false,
  logErrors: boolean = true,
): Return {
  try {
    const isFreeFromPath = slug[slug.length - 1] === 'free';
    if (isFreeFromPath) {
      slug = slug.slice(0, -1);
    }

    if (slug[0] === 'search' && wlMode === true && slug.length === 1) {
      slug = [];
    }
    const { result: geo, slug: slugAfterGeo } = getGeoFromString(slug, dicts.geoDict, wlMode);
    const { result: categories, slug: slugAfterCategories } = getCategoryFromString(
      slugAfterGeo,
      dicts.categoryNameDict,
    );

    const paramsParsingResult = getParametersFromPath(
      slugAfterCategories,
      searchParams,
      categories,
      dicts.categoryIdDict,
    );
    let { result: params } = paramsParsingResult;
    let { commonParams, hasAnyParamInPath } = paramsParsingResult;
    const { pathParams } = paramsParsingResult;

    if (isFreeFromPath) {
      commonParams = {
        ...commonParams,
        isFree: true,
        minPrice: undefined,
        maxPrice: undefined,
      };
      hasAnyParamInPath = true;
    }

    params = params.map((par) => {
      if ('unparsedLangValue' in par) {
        return {
          id: par.id,
          value: par.unparsedLangValue[locale],
        };
      }
      return par;
    });
    const breadcrumbs = getCategoriesBreadcrumbs(slug, slugAfterGeo, categories, dicts.categoryIdDict, locale);
    appendSeoParamsBreadcrumbs(breadcrumbs, pathParams);

    return {
      hasAnyParamInPath,
      geo: geo.value,
      categories,
      params,
      commonParams,
      breadcrumbs: breadcrumbs,
      pathParams,
    };
  } catch (e: any) {
    if (logErrors) {
      console.log('e', e.message, slug.join('/'));
    }
    throw e;
  }
}

type SubParsePart<T> = {
  slug: string[];
  result: T;
};

function getCategoryPath(category: CategoryItem, categoriesIdDict: CategoryIdDict): DictCategory[] {
  const startCategory = categoriesIdDict[category.id];
  let curCat = startCategory;
  const result = [curCat];
  while (true) {
    const parentId = categoriesIdDict[curCat.id]?.parentId;
    if (!parentId) {
      break;
    }
    curCat = categoriesIdDict[parentId];
    result.push(curCat);
  }

  return result.reverse();
}

function getCategoriesBreadcrumbs(
  slug: string[],
  slugAfterGeo: string[],
  categories: CategoryItem[],
  categoriesIdDict: CategoryIdDict,
  locale: string,
): Breadcrumb[] {
  let baseUrl = '';

  if (slug[0] && !slugAfterGeo.includes(slug[0])) {
    baseUrl += `/${slug[0]}`;
  }
  if (slug[1] && !slugAfterGeo.includes(slug[1])) {
    baseUrl += `/${slug[1]}`;
  }

  const result: Breadcrumb[] = [];
  for (let i = 0; i < categories.length; i++) {
    const catPath = getCategoryPath(categories[i], categoriesIdDict);
    const names = catPath.map((cat) => cat.link).join('/');
    const breadcrumb = {
      url: names,
      name: catPath[catPath.length - 1].name as string,
    };
    result.push(breadcrumb);
  }

  return result;
}

/**
 *
 * @param geoElem
 * тут крч на каждом шаге надо откусывать уже распаршенные куски от урла.
 * Выяснили гео - откусили кусок гео и передали категориям.
 * Категории нашли все категории подходящие - откусили их имена, и пошли дальше
 * потом так же параметры.
 */

type GeoReturn = SubParsePart<{ value: GeoItem; country: string }>;

function getGeoFromString(slug: string[], geoDict: GeoDict, wlMode: boolean = false): GeoReturn {
  // just get geo elem from the map and that all
  let copySlug = [...slug];

  if (wlMode) {
    if (!WL_COUNTRY) {
      throw new Error('incorrect WL settings');
    }

    copySlug = [WL_COUNTRY, ...copySlug];
  }

  const firstGeoElemText = copySlug.shift();

  const firstGeoFound = geoDict[firstGeoElemText || ''];

  if (!firstGeoFound) {
    throw new Error(`incorrect geo ${slug.join('/')}`);
  }
  if (firstGeoFound.t === GeoType.country) {
    const secondGeoElemText = copySlug.shift();
    const secondGeoFound = geoDict[secondGeoElemText || ''];

    if (!secondGeoFound) {
      if (secondGeoElemText) copySlug.unshift(secondGeoElemText || '');
      return {
        result: {
          value: {
            countryId: firstGeoFound.id,
            name: firstGeoElemText || '',
          },

          country: firstGeoFound.id,
        },
        slug: copySlug,
      };
    }

    if (secondGeoFound.t === GeoType.region) {
      const parentName = secondGeoFound.pn;
      if (parentName !== firstGeoElemText) {
        throw new Error(`incorrect geo ${slug.join('/')}`);
      }

      return {
        result: {
          value: {
            regionId: secondGeoFound.id,
            name: secondGeoElemText || '',
          },
          country: firstGeoFound.id,
        },
        slug: copySlug,
      };
    }

    if (secondGeoFound.t === GeoType.city) {
      const regionName = secondGeoFound.pn;
      if (!regionName) {
        throw new Error(`incorrect geo ${slug.join('/')}`);
      }
      const countryName = geoDict[regionName].pn;

      if (countryName !== firstGeoElemText) {
        throw new Error(`incorrect geo ${slug.join('/')}`);
      }

      return {
        result: {
          value: {
            cityId: secondGeoFound.id,
            name: secondGeoElemText || '',
          },
          country: firstGeoFound.id,
        },
        slug: copySlug,
      };
    }
  }

  throw new Error(`incorrect geo ${slug.join('/')}`);
}

function getCategoryFromString(initialSlug: string[], categoryDict: CategoryNameDict): SubParsePart<CategoryItem[]> {
  const slug = [...initialSlug];
  if (slug.length === 0) {
    return {
      result: [],
      slug,
    };
  }

  const startingCategoryName = slug[0];
  let startingCategoryArr = categoryDict[startingCategoryName || ''];
  if (!startingCategoryArr) {
    throw new Error(`incorrect category ${initialSlug.join('/')}`);
  }
  startingCategoryArr = startingCategoryArr.filter((val) => !val.parentId);

  if (!startingCategoryArr || !Array.isArray(startingCategoryArr) || startingCategoryArr?.length !== 1) {
    throw new Error(`incorrect category ${initialSlug.join('/')}`);
  }
  const startingCategory = startingCategoryArr[0];
  if (startingCategory.parentId) {
    throw new Error(`incorrect category ${initialSlug.join('/')}`);
  }

  const items: DictCategory[] = [];
  items.push(startingCategory);
  slug.shift();

  let activeCategory = startingCategory;

  while (true) {
    const nextUrlStep = slug.shift();

    const nextUrlCategoryArr = categoryDict[nextUrlStep || ''];
    if (!nextUrlCategoryArr || !nextUrlCategoryArr?.length) {
      if (nextUrlStep) slug.unshift(nextUrlStep);
      break;
    }

    const nextUrlCategory = nextUrlCategoryArr.find((val) => val.parentId === activeCategory.id);

    if (!nextUrlCategory) {
      if (nextUrlStep) {
        slug.unshift(nextUrlStep);
      }
      break;
    }

    if (nextUrlCategory?.parentId !== activeCategory.id && nextUrlStep) {
      //if we found a category next step, but its id is not correct to the parent - just place it back to url
      // it may be a parameter
      slug.unshift(nextUrlStep);
      break;
    }

    items.push(nextUrlCategory);
    activeCategory = nextUrlCategory;
  }

  return {
    result: items.map((val) => ({ id: val.id, name: val.name || '' })),
    slug,
  };
}

type Parameter = {
  id: string;
  value: string | string[] | { from?: number; to?: number };
};

type MLanguageParam = {
  id: string;
  unparsedLangValue: {
    [key: string]: string;
  };
  value: string;
};

type PathParamBreadcrumb = {
  id: string;
  name: string;
  searchKey: string;
};

type ParametersParsingReturn = {
  result: (Parameter | MLanguageParam)[];
  commonParams: CommonParams;
  hasAnyParamInPath: boolean;
  pathParams: PathParamBreadcrumb[];
};

function getParametersFromPath(
  initialSlug: string[],
  searchParams: URLSearchParams,
  categoriesFound: CategoryItem[],
  categoriesIdDict: CategoryIdDict,
): ParametersParsingReturn {
  const slug = [...initialSlug];
  const lowLevelCategoryId = categoriesFound[categoriesFound.length - 1]?.id;

  const foundLowLevelCategory = categoriesIdDict[lowLevelCategoryId];

  const { flat: allParams, dict: allParamsDict } = convertCategoryParametersToFlat(
    foundLowLevelCategory?.parameters || [],
  );

  let currentPriority = 0;
  const result: (Parameter | MLanguageParam)[] = [];
  let resultCommonParams: CommonParams = {};

  let hasAnyParamInPath = false;
  const pathParams: PathParamBreadcrumb[] = [];

  const searchParamsInObject = searchParamsToObject(searchParams);

  for (const key in searchParamsInObject) {
    if (key === 'isfree') {
      const value = searchParamsInObject[key];

      if (value === 'true' || value === 'false') {
        resultCommonParams = {
          ...resultCommonParams,
          isFree: value === 'true',
        };
      }
      continue;
    }

    if (commonParamsKeys.includes(key)) {
      resultCommonParams = {
        ...resultCommonParams,
        [key]: searchParamsInObject[key],
      };
      continue;
    }

    const param = allParams.find((par) => par.normalizedName === key); // allParamsDict[key];

    if (param) {
      if (param.type === 'NUMBER') {
        const from = searchParamsInObject[key]['from'] || undefined;
        const to = searchParamsInObject[key]['to'] || undefined;
        result.push({
          id: param.id,
          value: {
            from,
            to,
          },
        });
        continue;
      }
      result.push({
        id: param.id,
        value: searchParamsInObject[key],
      });
    }
  }

  if (resultCommonParams.isFree === true) {
    resultCommonParams = {
      ...resultCommonParams,
      minPrice: undefined,
      maxPrice: undefined,
    };
  }

  // решить багу с выбором значения не из енам серч ключей, а из языковых параметров.
  // Наверное возврашать вообще весь словарь, и определять язык на уровень выше
  slug.forEach((slugElem) => {
    const param = allParams.find((val) => val.enumSearchKeys?.includes(slugElem));

    if (!param?.isUsedInUrl) {
      throw new Error('incorrect category params ' + slugElem + ' ' + param?.name);
    }

    if ((param?.order || 0) <= currentPriority) {
      throw new Error('incorrect category params ' + slugElem + ' ' + param?.name);
    }

    currentPriority = param.order;

    const valIndex = param.enumSearchKeys?.findIndex((par) => par === slugElem);

    hasAnyParamInPath = true;
    const displayValue = param.enumOptions?.[valIndex!] || slugElem;
    result.push({
      id: param.id,
      value: displayValue,
    });
    pathParams.push({
      id: param.id,
      name: displayValue,
      searchKey: slugElem,
    });
  });

  // validate that dynamic params has correct parents and they're selected
  for (const resultElem of result) {
    const param = allParamsDict[resultElem.id];

    if (param.parentId && 'dynamicVariant' in param) {
      const parentParam = allParamsDict[param.parentId];
      if (!parentParam) {
        throw new Error(`incorrect category params ${initialSlug.join('/')}`);
      }

      const parentInResult = result.find((elem) => elem.id === parentParam.id);

      let value = parentInResult?.value;

      if (Array.isArray(value)) {
        if (value.length !== 1) {
          throw new Error(`incorrect category params ${initialSlug.join('/')}`);
        }

        value = value[0];

        if (param.dynamicVariant !== value) {
          throw new Error(`incorrect category params ${initialSlug.join('/')}`);
        }
      }
    }
  }

  return {
    hasAnyParamInPath,
    result,
    commonParams: resultCommonParams,
    pathParams,
  };
}

export function getBreadcrumbsBase(
  slug: string[],
  categoryId: string,
  dicts: Dicts,
  locale: string,
  wlMode: boolean = false,
) {
  const { slug: slugAfterGeo } = getGeoFromString(slug, dicts.geoDict, wlMode);

  let activeCat = dicts.categoryIdDict[categoryId];
  let categories = [{ id: categoryId, name: activeCat.link }];
  while (true) {
    const parentId = activeCat.parentId;
    if (!parentId) {
      break;
    }

    activeCat = dicts.categoryIdDict[parentId];
    categories.push({ id: activeCat.id, name: activeCat.link });
  }
  categories = categories.reverse();

  const breadcrumbs = getCategoriesBreadcrumbs(slug, slugAfterGeo, categories, dicts.categoryIdDict, locale);

  return breadcrumbs;
}

type SeoPathParam = { name: string; searchKey: string };

export function appendSeoParamsBreadcrumbs(breadcrumbs: Breadcrumb[], pathParams: SeoPathParam[]): Breadcrumb[] {
  if (pathParams.length === 0 || breadcrumbs.length === 0) {
    return breadcrumbs;
  }

  let acc = breadcrumbs[breadcrumbs.length - 1].url;
  for (const pathParam of pathParams) {
    acc = `${acc}/${pathParam.searchKey}`;
    breadcrumbs.push({ url: acc, name: pathParam.name });
  }

  return breadcrumbs;
}

export function appendFreeBreadcrumb(breadcrumbs: Breadcrumb[], name: string): Breadcrumb[] {
  if (breadcrumbs.length === 0) {
    return breadcrumbs;
  }

  breadcrumbs.push({ url: `${breadcrumbs[breadcrumbs.length - 1].url}/free`, name });

  return breadcrumbs;
}

export function getSeoPathParamsFromProductValues(
  parameterValues: TelegramProductParameterValueResponseDto[],
): SeoPathParam[] {
  const collected: (SeoPathParam & { order: number })[] = [];

  for (const parameterValue of parameterValues) {
    const definition = parameterValue.parameter;
    if (!definition?.isUsedInUrl) {
      continue;
    }

    const values = parameterValue.values;
    if (Array.isArray(values) && values.length > 1) {
      continue;
    }
    const value = Array.isArray(values) && values.length === 1 ? values[0] : parameterValue.value;
    if (!value) {
      continue;
    }

    const valueIndex = findArrValueIndex(definition.enumOptions, value);
    if (valueIndex === -1) {
      continue;
    }

    const searchKey = definition.enumSearchKeys?.[valueIndex];
    if (!searchKey) {
      continue;
    }

    collected.push({
      name: definition.enumOptions?.[valueIndex] || value,
      searchKey,
      order: definition.order,
    });
  }

  const sorted = collected.sort((a, b) => a.order - b.order);
  const result: SeoPathParam[] = [];
  let lastOrder = 0;
  for (const item of sorted) {
    if (item.order <= lastOrder) {
      continue;
    }
    lastOrder = item.order;
    result.push({ name: item.name, searchKey: item.searchKey });
  }

  return result;
}

export function filterResolvableBreadcrumbs(
  breadcrumbs: Breadcrumb[],
  userGeoSlug: string[],
  dicts: Dicts,
  locale: string,
  wlMode: boolean = false,
): Breadcrumb[] {
  const resolved: Breadcrumb[] = [];

  for (const breadcrumb of breadcrumbs) {
    const slug = [...userGeoSlug, ...breadcrumb.url.split('/').filter(Boolean)];

    if (
      slug.length === 0 ||
      slug.length > MAX_SLUG_DEPTH ||
      slug.some((segment) => !LEGIT_SLUG_SEGMENT.test(segment))
    ) {
      break;
    }

    try {
      convertPathToParams(slug, new URLSearchParams(), dicts, locale, wlMode, false);
    } catch {
      break;
    }

    resolved.push(breadcrumb);
  }

  return resolved;
}
